/**
 * ChatPanel component
 * Simple, stable layout
 */
import React, { memo } from 'react';
import { Box, Text } from 'ink';
import Spinner from 'ink-spinner';
import { MessageItem } from './MessageItem.js';
import { InputBox } from './InputBox.js';
import { SlashCommand } from './SlashCommandMenu.js';
import type { ChatMessage } from '../../../types/llm.types.js';

interface ChatPanelProps {
    messages: ChatMessage[];
    isStreaming: boolean;
    inputValue: string;
    onInputChange: (value: string) => void;
    onSubmit: (value: string) => void;
    inputFocused?: boolean;
    showSlashMenu?: boolean;
    slashCommands?: SlashCommand[];
    slashMenuIndex?: number;
    minHeight?: number;
}

// Memoized message list to prevent re-renders
const MessageList = memo(({ messages }: { messages: ChatMessage[] }) => {
    const visible = messages.slice(-8);
    const hidden = messages.length - visible.length;

    if (visible.length === 0) {
        return (
            <Box flexDirection="column" alignItems="center" padding={1}>
                <Text dimColor>Start a conversation...</Text>
                <Text dimColor>Type / for commands</Text>
            </Box>
        );
    }

    return (
        <>
            {hidden > 0 && <Text dimColor>({hidden} earlier)</Text>}
            {visible.map((m) => <MessageItem key={m.id} message={m} />)}
        </>
    );
});

// Memoized slash menu
const SlashMenu = memo(({ commands, index }: { commands: SlashCommand[]; index: number }) => (
    <Box flexDirection="column" borderStyle="round" borderColor="cyan" paddingX={1}>
        <Text bold color="cyan">Commands</Text>
        {commands.map((cmd, i) => (
            <Text
                key={cmd.name}
                backgroundColor={i === index ? 'cyan' : undefined}
                color={i === index ? 'black' : 'white'}
            >
                {i === index ? '› ' : '  '}/{cmd.name} - {cmd.description}
            </Text>
        ))}
    </Box>
));

export const ChatPanel: React.FC<ChatPanelProps> = ({
    messages,
    isStreaming,
    inputValue,
    onInputChange,
    onSubmit,
    inputFocused = true,
    showSlashMenu = false,
    slashCommands = [],
    slashMenuIndex = 0,
    minHeight = 15,
}) => {
    // Subtract space for input box (~3 lines) and slash menu if shown (~5 lines)
    const messagesMinHeight = Math.max(5, minHeight - 4 - (showSlashMenu ? 5 : 0));

    return (
        <Box flexDirection="column">
            {/* Messages */}
            <Box flexDirection="column" borderStyle="round" borderColor="gray" padding={1} minHeight={messagesMinHeight}>
                <MessageList messages={messages} />
                {isStreaming && (
                    <Text color="green"><Spinner type="dots" /> Responding...</Text>
                )}
            </Box>

            {/* Slash menu */}
            {showSlashMenu && slashCommands.length > 0 && (
                <SlashMenu commands={slashCommands} index={slashMenuIndex} />
            )}

            {/* Input */}
            <InputBox
                value={inputValue}
                onChange={onInputChange}
                onSubmit={onSubmit}
                disabled={isStreaming}
                focused={inputFocused}
            />
        </Box>
    );
};
